"use client"

import type { ComponentPropsWithoutRef } from "react"
import * as React from "react"
import type { Dialog as DialogPrimitive } from "radix-ui"
import { Command as CommandPrimitive } from "cmdk"
import { Search } from "lucide-react"

import { cn } from "../lib/utils"
import { Dialog, DialogContent } from "./ShadcnDialog"

const Command = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive>
>(({ className, ...props }, ref) => (
  <CommandPrimitive
    ref={ref}
    className={cn(
      "flex h-full w-full flex-col overflow-hidden rounded-md bg-popover text-popover-foreground",
      className,
    )}
    {...props}
  />
))
Command.displayName = CommandPrimitive.displayName

type CommandAppearance = "default" | "polished"

const CommandAppearanceContext = React.createContext<CommandAppearance>("default")

type CommandDialogProps = DialogPrimitive.DialogProps &
  Pick<ComponentPropsWithoutRef<typeof Command>, "onKeyDown"> & {
    appearance?: CommandAppearance
  }

const CommandDialog = ({ appearance = "default", children, onKeyDown, ...props }: CommandDialogProps) => {
  const isPolished = appearance === "polished"

  return (
    <Dialog {...props}>
      <DialogContent
        showClose={!isPolished}
        className={cn(
          "overflow-hidden p-0 shadow-lg",
          isPolished &&
            "w-[calc(100%-2rem)] max-w-[640px] rounded-2xl border-border/70 bg-popover/95 shadow-2xl backdrop-blur-xl",
        )}
      >
        <CommandAppearanceContext.Provider value={appearance}>
          <Command
            onKeyDown={onKeyDown}
            className={cn(
              "[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5",
              isPolished &&
                "[&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-1 [&_[cmdk-group]]:px-3 [&_[cmdk-group]]:py-2 [&_[cmdk-item]]:py-2 [&_[cmdk-item]_svg]:h-4 [&_[cmdk-item]_svg]:w-4",
            )}
          >
            {children}
            {isPolished && (
              <div className="sticky bottom-0 flex items-center justify-center gap-4 border-t border-border/60 bg-popover/95 px-4 py-2.5 text-xs text-muted-foreground backdrop-blur-xl">
                <span className="inline-flex items-center gap-1.5">
                  <kbd className="min-w-5 rounded border border-border bg-muted/70 px-1.5 py-0.5 text-center text-[10px] leading-3 text-foreground">
                    ↑↓
                  </kbd>
                  Navigate
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <kbd className="min-w-5 rounded border border-border bg-muted/70 px-1.5 py-0.5 text-center text-[10px] leading-3 text-foreground">
                    ↵
                  </kbd>
                  Open
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <kbd className="min-w-5 rounded border border-border bg-muted/70 px-1.5 py-0.5 text-center text-[10px] leading-3 text-foreground">
                    Esc
                  </kbd>
                  Close
                </span>
              </div>
            )}
          </Command>
        </CommandAppearanceContext.Provider>
      </DialogContent>
    </Dialog>
  )
}

const CommandInput = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Input>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input>
>(({ className, ...props }, ref) => {
  const appearance = React.useContext(CommandAppearanceContext)
  const isPolished = appearance === "polished"

  return (
    <div
      className={cn("flex items-center border-b px-3", isPolished && "relative border-border/70 px-4")}
      // eslint-disable-next-line react/no-unknown-property -- cmdk-input-wrapper is a valid cmdk custom attribute
      cmdk-input-wrapper=""
    >
      <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
      <CommandPrimitive.Input
        ref={ref}
        className={cn(
          "flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-hidden placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50",
          isPolished && "h-12 pr-12",
          className,
        )}
        {...props}
      />
      {isPolished && (
        <kbd className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 rounded border border-border bg-muted/70 px-1.5 py-0.5 text-[10px] leading-3 text-muted-foreground">
          Esc
        </kbd>
      )}
    </div>
  )
})

CommandInput.displayName = CommandPrimitive.Input.displayName

const CommandList = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.List>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.List
    ref={ref}
    className={cn("max-h-[300px] overflow-y-auto overflow-x-hidden", className)}
    {...props}
  />
))

CommandList.displayName = CommandPrimitive.List.displayName

const CommandEmpty = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Empty>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Empty>
>((props, ref) => <CommandPrimitive.Empty ref={ref} className="py-6 text-center text-sm" {...props} />)

CommandEmpty.displayName = CommandPrimitive.Empty.displayName

const CommandGroup = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Group>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Group>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Group
    ref={ref}
    className={cn(
      "overflow-hidden p-1 text-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground",
      className,
    )}
    {...props}
  />
))

CommandGroup.displayName = CommandPrimitive.Group.displayName

const CommandSeparator = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Separator ref={ref} className={cn("-mx-1 h-px bg-border", className)} {...props} />
))
CommandSeparator.displayName = CommandPrimitive.Separator.displayName

const CommandItem = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Item>
>(({ className, ...props }, ref) => {
  const appearance = React.useContext(CommandAppearanceContext)

  return (
    <CommandPrimitive.Item
      ref={ref}
      className={cn(
        "relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-hidden aria-selected:bg-accent aria-selected:text-accent-foreground data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50",
        appearance === "polished" && "rounded-md transition-colors aria-selected:bg-accent/70",
        className,
      )}
      {...props}
    />
  )
})

CommandItem.displayName = CommandPrimitive.Item.displayName

const CommandShortcut = ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => {
  return <span className={cn("ml-auto text-xs tracking-widest text-muted-foreground", className)} {...props} />
}
CommandShortcut.displayName = "CommandShortcut"

export {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
}
